import {
  useContext,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import {
  ComboBoxStateContext,
  type ComboBoxState,
  type Key,
} from "react-aria-components";
import { useEffectEvent } from "../helpers/useEffectEvent.js";
import type { ComboboxSelectionMode } from "./combobox.js";

/**
 * How long typing has to pause before the highlight is handed to screen
 * readers. react-aria's own Autocomplete uses the same delay for the same
 * reason.
 */
const announceDelayMs = 500;

/**
 * Removes the `aria-activedescendant` react-aria put on the input: react-aria's
 * `mergeProps` keeps the value it already has when a component passes
 * `undefined`, and React omits an attribute whose value is null. Typed as
 * `undefined` because that is what the DOM prop accepts.
 */
const hiddenActiveDescendant = null as unknown as undefined;

export interface ComboboxAutoHighlight {
  /**
   * For the field's input. `undefined` leaves react-aria's own value alone;
   * anything else replaces it.
   */
  activeDescendant: string | undefined;
  /** For the field input's `onKeyDownCapture`. */
  handleKeyDownCapture: (event: KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Keeps the first matching option highlighted while the user types, so Enter
 * inserts it without arrowing down first — the behavior of the select this
 * family replaced.
 *
 * react-aria deliberately doesn't do this: it once did, and NVDA turned out to
 * stop announcing typing and caret movement in the input for as long as any
 * option holds virtual focus through `aria-activedescendant`. So the highlight
 * is split in two here. The *focused key* is set immediately, which is what
 * paints the option and what Enter commits; the *activedescendant* — the half
 * screen readers react to — is withheld while the text is being edited and
 * appears once typing pauses, or as soon as the user takes over the navigation
 * with an arrow key. That is the compromise react-aria's own Autocomplete
 * settled on.
 */
export function useComboboxAutoHighlight(): ComboboxAutoHighlight {
  // The context is typed over `any` items; narrow to `object` for safe use.
  const state = useContext(ComboBoxStateContext) as ComboBoxState<
    object,
    ComboboxSelectionMode
  > | null;
  const inputValue = state?.inputValue ?? "";
  const focusedKey = state?.selectionManager.focusedKey ?? null;

  // Always the current state, for the effect and handler below: the state
  // object itself is new on every render, so nothing may close over it.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Set while there is a search to highlight the top match of; cleared when the
  // field empties, and when Tab leaves without taking it.
  const wantsHighlight = useRef(false);
  // The key this hook highlighted, to tell it apart from one the user arrowed
  // to. Read during render below, which is safe because every change to it
  // comes with a react-aria state change that rerenders anyway.
  const highlightedKey = useRef<Key | null>(null);
  const lastInputValue = useRef(inputValue);
  const [isEditingText, setEditingText] = useState(false);
  const announceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    return () => {
      clearTimeout(announceTimer.current);
    };
  }, []);

  // Restarts the window in which the highlight stays off screen readers.
  const markEditing = useEffectEvent(() => {
    setEditingText(true);
    clearTimeout(announceTimer.current);
    announceTimer.current = setTimeout(() => {
      setEditingText(false);
    }, announceDelayMs);
  });

  useEffect(() => {
    if (inputValue === lastInputValue.current) {
      return;
    }
    lastInputValue.current = inputValue;

    // An empty field is back to showing every option, where react-aria
    // highlights the selected one — nothing to take over.
    wantsHighlight.current = inputValue !== "";

    // Deletions get the highlight as well as insertions do: react-aria's
    // Autocomplete drops it there to keep NVDA announcing, which this doesn't
    // have to do because the announced half is already withheld below.
    markEditing();
  }, [inputValue, markEditing]);

  // Deliberately not keyed on anything: react-aria clears the focused key from
  // its own effect, which runs after this one, so its clear and this hook's
  // write can land in the same commit — leaving a dependency list unchanged
  // and this effect never re-running. Re-checking after every render costs a
  // comparison and converges in one more pass: nothing else moves the key
  // once it holds.
  useEffect(() => {
    const current = stateRef.current;
    if (!wantsHighlight.current || !current || !current.isOpen) {
      return;
    }

    // Something is highlighted — this hook's own pick, an option the user
    // arrowed to, or one react-aria focused itself. Never fight it: the intent
    // only ever applies to an empty highlight, and every text change clears one
    // and renews the intent.
    if (current.selectionManager.focusedKey !== null) {
      return;
    }

    const firstKey = findFirstInsertableItemKey(current);
    if (firstKey === null) {
      // No option to highlight yet — the collection may still be loading. The
      // intent stays set, so a later render picks it up.
      return;
    }

    highlightedKey.current = firstKey;
    // The focused key is only exposed — as a highlight, and through
    // aria-activedescendant — while the selection manager is itself marked
    // focused. Typing has usually done that already; this makes it hold
    // however the popup was opened.
    current.selectionManager.setFocused(true);
    current.selectionManager.setFocusedKey(firstKey);
  });

  // Capture phase, so it runs before react-aria's own Tab handling: tabbing out
  // moves on without committing the highlighted option, the way clicking
  // outside does. Giving up the intent matters as much as clearing the key —
  // the effect above would otherwise put the highlight straight back.
  const handleKeyDownCapture = (event: KeyboardEvent<HTMLInputElement>) => {
    // Moving the caret is the other thing NVDA stops announcing while an
    // option holds virtual focus — react-aria drops the focus outright on
    // these keys. The highlight is the top match rather than a navigation
    // state, so it stays; what restarts is the quiet window.
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      markEditing();
      return;
    }

    if (event.key !== "Tab") {
      return;
    }
    wantsHighlight.current = false;
    stateRef.current?.selectionManager.setFocusedKey(null);
  };

  const isOwnHighlight =
    focusedKey !== null && focusedKey === highlightedKey.current;

  return {
    activeDescendant:
      isEditingText && isOwnHighlight ? hiddenActiveDescendant : undefined,
    handleKeyDownCapture,
  };
}

/**
 * The option Enter should insert while typing: the first that isn't disabled,
 * and in multi-select also the first that isn't already a chip — Enter on an
 * option that is already selected would toggle it off, the opposite of what
 * typing its name and pressing Enter reads as. Single-select must not skip it:
 * with "Apple" selected, typing "Ap" would move Enter onto "Apricot".
 */
function findFirstInsertableItemKey(
  state: ComboBoxState<object, ComboboxSelectionMode>,
): Key | null {
  const { collection, selectionManager } = state;
  const skipsSelected = selectionManager.selectionMode === "multiple";
  const isInsertable = (key: Key) =>
    !selectionManager.isDisabled(key) &&
    !(skipsSelected && selectionManager.isSelected(key));

  for (const node of collection) {
    if (node.type === "item" && isInsertable(node.key)) {
      return node.key;
    }
    for (const child of collection.getChildren?.(node.key) ?? []) {
      if (child.type === "item" && isInsertable(child.key)) {
        return child.key;
      }
    }
  }
  return null;
}
